// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import "modernizr";
import * as React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import { RootPath } from "types";

import { sagaMiddleware, store } from "states/store";
import mySaga from "sagas/sagas";
import { CloudSagas } from "sagas/cloudsagas";

import IntlProvider from "view/comp/IntlProvider";
import Root, { childRoutes } from "view/layout/Root";

// application cache handler
function reloadApp() {
  console.log("Detected new application version. Performing reloading to activate it.");
  try {
    (window as any).applicationCache.swapCache();
  } catch {
    console.warn("Error swapping application cache.");
  }
  window.location.reload();
}
// application cache may not be supported (may even be deactivated in future...)
if ((window as any).applicationCache) {
  (window as any).applicationCache.addEventListener(
    "updateready",
    (_event: Event) => {
      reloadApp();
    },
    false,
  );
  // check for race condition if cache is already ready
  if ((window as any).applicationCache.status === (window as any).applicationCache.UPDATEREADY) {
    // if there is a cache update already
    reloadApp();
  }
}

function startApplication() {
  // get routes
  const router = createBrowserRouter(
    [
      {
        path: "/",
        element: <Root />,
        errorElement: <h1>This side is not available.</h1>,
        children: childRoutes,
      },
      {
        path: `/${RootPath.RFID}`,
        element: <Root />,
        errorElement: <h1>This rfid page is not available.</h1>,
        children: childRoutes,
      },
      {
        path: `/${RootPath.OCPP}`,
        element: <Root />,
        errorElement: <h1>This ocpp page is not available.</h1>,
        children: childRoutes,
      },
      {
        path: `/${RootPath.TREND}`,
        element: <Root />,
        errorElement: <h1>This trend page is not available.</h1>,
        children: childRoutes,
      },
    ],
    {
      future: {
        v7_relativeSplatPath: true, // fix buggy relative path resolution in splat routes
      },
    },
  );
  // add router to saga
  sagaMiddleware.setContext({
    router,
  });

  const container = document.getElementById("root");
  const root = createRoot(container!);
  root.render(
    <Provider store={store}>
      <IntlProvider>
        <RouterProvider router={router} />
      </IntlProvider>
    </Provider>,
  );

  // run the saga
  const apiSagas = new CloudSagas();
  sagaMiddleware.run(mySaga.bind(null, store, apiSagas));
}

// main entry point

// perform check on compatible browser as very first step to avoid abortion on JS errors
if (
  !Modernizr.promises ||
  !Modernizr.es5string ||
  !Modernizr.es5object ||
  !Modernizr.fetch ||
  !Modernizr.intl
) {
  alert(
    "Your web browser is not supported since it does not provide all functionality required.\n\n" +
      "Please use another browser for this web application.",
  );
  throw new Error("Browser not supported.");
} else {
  // everything ready
  startApplication();
}
